import type { LogEntry, LogSource } from "@skaile/workspaces/types";
import {
  isStreamUsable,
  isUnwritableStreamError,
  markStreamBroken,
  type OutputStream,
} from "../stream-health.js";
import type { LogSink } from "./types.js";

export type StdoutFormat = "pretty" | "json";

/** Which standard stream the sink writes to. */
export type StdoutStream = "stdout" | "stderr";

export type { OutputStream };

export interface StdoutSinkOptions {
  format?: StdoutFormat;
  /**
   * Standard stream to write to. Default: `"stdout"`. Set to `"stderr"` for
   * processes whose stdout is a reserved channel — e.g. a stdio MCP server,
   * where stdout carries JSON-RPC and any log line would corrupt the protocol.
   * An explicit stream object may be passed instead; used by tests.
   */
  stream?: StdoutStream | OutputStream;
}

function formatSource(source: LogSource): string {
  const parts = [source.kind, source.subkind];
  if (source.instance) parts.push(source.instance);
  return `[${parts.join(":")}]`;
}

function formatTime(iso: string): string {
  // HH:MM:SS.sss
  return iso.slice(11, 23);
}

const LEVEL_LABEL = {
  debug: "DEBUG",
  info: "INFO ",
  warn: "WARN ",
  error: "ERROR",
} as const;

export class StdoutSink implements LogSink {
  private format: StdoutFormat;
  private stream: OutputStream;

  constructor(opts: StdoutSinkOptions = {}) {
    this.stream = resolveStream(opts.stream);
    this.format = opts.format ?? (this.stream.isTTY ? "pretty" : "json");
  }

  write(entry: LogEntry): void {
    if (!this.writable()) return;
    try {
      this.stream.write(`${this.render(entry)}\n`);
    } catch (err) {
      // Sync throws (write-after-end, destroyed) never reach the `error` event.
      if (isUnwritableStreamError(err)) markStreamBroken(this.stream);
    }
  }

  writeBatch(entries: LogEntry[]): void {
    // `write` re-checks per entry, so a batch stops at the first failure.
    for (const e of entries) this.write(e);
  }

  close(): void {}

  /** False once the far end is gone; the sink then drops entries silently. */
  private writable(): boolean {
    return isStreamUsable(this.stream);
  }

  private render(entry: LogEntry): string {
    if (this.format === "json") {
      return JSON.stringify(entry);
    }
    const head = `${formatTime(entry.timestamp)}  ${LEVEL_LABEL[entry.level]}  ${formatSource(entry.source)}`;
    const tail = entry.message;
    const detail = entry.data ? `\n  > ${JSON.stringify(entry.data)}` : "";
    const err = entry.error ? `\n  ! ${entry.error.name}: ${entry.error.message}` : "";
    return `${head}  ${tail}${detail}${err}`;
  }
}

function resolveStream(stream: StdoutSinkOptions["stream"]): OutputStream {
  if (stream === "stderr") return process.stderr;
  if (stream === undefined || stream === "stdout") return process.stdout;
  return stream;
}
